import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { Table } from '../../common/tanstacktable';
import { SystemClassifierItemDto } from '../../systemClassifier';
import { DataObjectView, IndicatorFormDataType } from '../types';
import { FC, memo, useMemo } from 'react';
import translations from '../translations/DataObjectIndicatorDetailsTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useSystemClassifiers } from '../../configuration/useSystemClassifiers';
import styled from 'styled-components';
import { Flex } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-object-indicator-view-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<IndicatorFormDataType>();
const EMPTY_DATA: IndicatorFormDataType[] = [];

export function DataObjectIndicatorDetailsTable({
  dataObjectView,
  indicators,
}: {
  dataObjectView: DataObjectView;
  indicators: IndicatorFormDataType[] | undefined;
}) {
  const { t } = useTranslation();

  const {
    INDICATOR_UNIT: { systemClassifierItems: indicatorUnits },
    MULTIPLIER: { systemClassifierItems: multipliers },
    PERIOD_DATA_TYPE: { systemClassifierItems: periodDataTypes },
  } = useSystemClassifiers('PERIOD_DATA_TYPE', 'MULTIPLIER', 'INDICATOR_UNIT');

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('code', {
        header: t('column.code.label'),
        meta: {
          width: '30ch',
        },
      }),
      columnHelper.accessor('name', {
        header: t('column.name.label'),
        meta: {
          width: '50%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('description', {
        header: t('column.description.label'),
        meta: {
          width: '50%',
          minWidth: 150,
        },
      }),
    ] as ColumnDef<IndicatorFormDataType, any>[];

    if (dataObjectView === DataObjectView.OUT) {
      cols.push(
        columnHelper.accessor('decimals', {
          header: t('column.decimals.label'),
          meta: {
            width: 135,
          },
        }),
      );
    }

    cols.push(
      columnHelper.accessor('periodTypeId', {
        header: t('column.periodType.label'),
        cell: (props) => (
          <SystemClassifierItemCell {...props} items={periodDataTypes} />
        ),
        filterFn: 'includesOption',
        meta: {
          width: 150,
        },
      }),
      columnHelper.accessor('multiplierId', {
        header: t('column.multiplier.label'),
        cell: (props) => (
          <SystemClassifierItemCell {...props} items={multipliers} />
        ),
        filterFn: 'includesOption',
        meta: {
          width: 120,
          textAlign: 'right',
        },
      }),
      columnHelper.accessor('unitId', {
        header: t('column.unit.label'),
        cell: (props) => (
          <SystemClassifierItemCell {...props} items={indicatorUnits} />
        ),
        filterFn: 'includesOption',
        meta: {
          width: 120,
        },
      }),
    );

    if (dataObjectView === DataObjectView.IN) {
      cols.push(
        columnHelper.accessor('required', {
          header: t('column.required.label'),
          cell: BooleanCell,
          meta: {
            width: 140,
          },
          filterFn: 'includesOption',
        }),
      );
      cols.push(
        columnHelper.accessor('imputeFromIndicator', {
          header: t('column.imputeFromIndicator.label'),
          cell: (props) => <IndicatorCell {...props} indicators={indicators} />,
          filterFn: 'includesString',
          meta: {
            width: 140,
          },
        }),
      );
    }

    if (dataObjectView === DataObjectView.OUT) {
      cols.push(
        columnHelper.accessor('intermediate', {
          header: t('column.intermediate.label'),
          cell: BooleanCell,
          meta: {
            width: 150,
          },
          filterFn: 'includesOption',
        }),
      );
    }

    cols.push(
      columnHelper.accessor('width', {
        header: t('column.width.label'),
        cell: memo((props) => <span>{props.getValue()}</span>),
        meta: {
          width: 135,
          textAlign: 'right',
        },
      }),
    );

    return cols;
  }, [
    dataObjectView,
    indicatorUnits,
    indicators,
    multipliers,
    periodDataTypes,
    t,
  ]);

  const options = useMemo(
    () => ({
      intermediate: [
        { id: true, name: t('column.intermediate.yes') },
        { id: false, name: t('column.intermediate.no') },
      ],
      periodTypeId: periodDataTypes,
      multiplierId: multipliers,
      required: [
        { id: true, name: t('column.required.yes') },
        { id: false, name: t('column.required.no') },
      ],
      unitId: indicatorUnits,
    }),
    [indicatorUnits, multipliers, periodDataTypes, t],
  );

  return (
    <TableWrapper vertical align="end" gap="middle">
      <Table
        name="indicators"
        data={indicators || EMPTY_DATA}
        columns={columns}
        options={options}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </TableWrapper>
  );
}

const SystemClassifierItemCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    items: SystemClassifierItemDto[];
  }
> = memo((props) => (
  <>
    {props.items?.find((item) => item.id === props.renderValue())?.name || ''}
  </>
));

const IndicatorCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    indicators: IndicatorFormDataType[] | undefined;
  }
> = memo((props) => {
  const indicator = props.indicators?.find(
    (item) => item.code === props.renderValue(),
  );
  return <>{indicator ? indicator.code + ' - ' + indicator.name : ''}</>;
});

const BooleanCell: FC<CellContext<IndicatorFormDataType, any>> = memo(
  (props) => {
    const { t } = useTranslation();
    return (
      <>
        {props.renderValue() ? t('column.boolean.yes') : t('column.boolean.no')}
      </>
    );
  },
);

export const TableWrapper = styled(Flex)`
  margin: auto;
`;
